import React from 'react';
import { usePortalContext } from '../context/PortalContext';
import { hasElevatedAnyRole, PORTAL_ROLES } from '../utils/roles';

interface SupplierTermsVersion {
  termsVersionId: string;
  versionLabel: string;
  plainEnglishText: string;
  legalDocumentText: string;
  isPlaceholder: boolean;
  isCurrent: boolean;
  publishedAt: string;
  publishedBy: string;
}

interface SupplierTermsHistory {
  termsVersionId: string;
  versionLabel: string;
  isPlaceholder: boolean;
  isCurrent: boolean;
  publishedAt: string;
  publishedBy: string;
}

const SupplierTermsAdmin = (): JSX.Element => {
  const { tenant } = usePortalContext();
  const hasAccess = hasElevatedAnyRole(tenant?.roles, [
    PORTAL_ROLES.documentManager,
    PORTAL_ROLES.marketingAdmin
  ]);
  const [current, setCurrent] = React.useState<SupplierTermsVersion | null>(null);
  const [versions, setVersions] = React.useState<SupplierTermsHistory[]>([]);
  const [versionLabel, setVersionLabel] = React.useState('');
  const [plainEnglishText, setPlainEnglishText] = React.useState('');
  const [legalDocumentText, setLegalDocumentText] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [publishing, setPublishing] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [message, setMessage] = React.useState<string | null>(null);
  const containsPlaceholderMarker = /PLACEHOLDER\s*[—-]\s*NOT APPROVED/i.test(legalDocumentText);

  const load = React.useCallback(async (): Promise<void> => {
    if (!hasAccess) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/admin/supplier-terms');
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.message || payload?.error || `Failed to load supplier terms (${response.status})`);
      }
      const nextCurrent = (payload?.current ?? null) as SupplierTermsVersion | null;
      setCurrent(nextCurrent);
      setVersions(Array.isArray(payload?.versions) ? payload.versions as SupplierTermsHistory[] : []);
      setPlainEnglishText(nextCurrent?.plainEnglishText ?? '');
      setLegalDocumentText(nextCurrent?.legalDocumentText ?? '');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [hasAccess]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const publish = async (): Promise<void> => {
    if (!window.confirm('Publish this as the current Supplier Collaboration Terms? Existing accepted versions will remain unchanged.')) {
      return;
    }
    setPublishing(true);
    setError(null);
    setMessage(null);
    try {
      const response = await fetch('/api/admin/supplier-terms', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          versionLabel,
          plainEnglishText,
          legalDocumentText,
          expectedCurrentTermsVersionId: current?.termsVersionId
        })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.message || payload?.error || `Failed to publish supplier terms (${response.status})`);
      }
      setMessage(`Supplier Collaboration Terms ${payload?.published?.versionLabel ?? versionLabel} published.`);
      setVersionLabel('');
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setPublishing(false);
    }
  };

  if (!hasAccess) {
    return (
      <div className="card">
        <h1 className="section-title">Supplier Legal Content</h1>
        <p className="muted">Document Manager or Marketing Admin access is required.</p>
      </div>
    );
  }

  return (
    <div>
      <h1 className="section-title">Supplier Legal Content</h1>
      <p className="muted">
        Maintain the plain-English registration explanation and the full legal document. Publishing creates a new immutable version so each supplier acceptance remains tied to the text they accepted.
      </p>

      {current?.isPlaceholder && (
        <div className="pill warning" style={{ marginBottom: '12px' }}>
          The current document is a development placeholder. Production supplier registration remains blocked until approved terms are published.
        </div>
      )}

      <div className="card" style={{ display: 'grid', gap: '12px' }}>
        <div>
          <strong>Current version:</strong> {current?.versionLabel ?? 'None'}
          {current && <span className="muted"> — published {new Date(current.publishedAt).toLocaleString()} by {current.publishedBy}</span>}
        </div>
        <a href="/supplier-terms" target="_blank" rel="noreferrer">Open the public full terms page</a>

        <label>
          <div className="muted" style={{ marginBottom: '4px' }}>New version label</div>
          <input
            className="input"
            value={versionLabel}
            onChange={(event) => setVersionLabel(event.target.value)}
            placeholder="2026-07-legal-approved"
            maxLength={64}
          />
        </label>

        {containsPlaceholderMarker && (
          <span className="pill warning">
            Replace the development placeholder marker and text with approved legal content before publishing.
          </span>
        )}

        <label>
          <div className="muted" style={{ marginBottom: '4px' }}>Plain-English explanation shown before acceptance</div>
          <textarea
            className="input"
            value={plainEnglishText}
            onChange={(event) => setPlainEnglishText(event.target.value)}
            rows={10}
            maxLength={12000}
          />
        </label>

        <label>
          <div className="muted" style={{ marginBottom: '4px' }}>Full legal document</div>
          <textarea
            className="input"
            value={legalDocumentText}
            onChange={(event) => setLegalDocumentText(event.target.value)}
            rows={24}
            maxLength={200000}
          />
        </label>

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            className="btn"
            type="button"
            onClick={() => { void publish(); }}
            disabled={publishing || loading || !current || !versionLabel.trim() || containsPlaceholderMarker || plainEnglishText.trim().length < 100 || legalDocumentText.trim().length < 100}
          >
            {publishing ? 'Publishing…' : 'Publish new version'}
          </button>
          <button className="btn secondary" type="button" onClick={() => { void load(); }} disabled={publishing || loading}>
            Reload current version
          </button>
        </div>
        {loading && <span className="badge-subtle">Loading supplier terms…</span>}
        {error && <span className="pill warning">{error}</span>}
        {message && <span className="pill success">{message}</span>}
      </div>

      <div className="card" style={{ marginTop: '12px' }}>
        <h3>Published versions</h3>
        {versions.length === 0 ? (
          <p className="muted">No versions found.</p>
        ) : (
          <ul>
            {versions.map((version) => (
              <li key={version.termsVersionId}>
                <strong>{version.versionLabel}</strong>{version.isCurrent ? ' — current' : ''}{version.isPlaceholder ? ' — placeholder' : ''}
                <span className="muted"> — {new Date(version.publishedAt).toLocaleString()} by {version.publishedBy}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default SupplierTermsAdmin;
